"use client";

import { useEffect, useState } from "react";
import { toast } from "@/shared/components/toast";
import { useAuthStore } from "@/modules/auth/stores/useAuthStore";
import { useAuthModal } from "@/modules/auth/stores/useAuthModal";
import { getActiveStoreId } from "@/modules/store/stores/useActiveStore";
import {
    cancelStockAlert,
    getStockAlertStatus,
    requestStockAlert,
} from "./stock-alert.service";
import { getApiErrorMessage } from "@/shared/helpers/errors";

/**
 * "Avise-me quando chegar" de um produto sem estoque (modo notify_me do
 * out_of_stock_display). Convidado abre o modal de login; logado registra/
 * cancela via stock-alert.service.
 *
 * `fetchStatus` liga a consulta do estado atual no mount (use na PDP; nos
 * cards de grade deixe desligado para não disparar um GET por card).
 */
export function useStockAlert(
    productId: number,
    { fetchStatus = false }: { fetchStatus?: boolean } = {}
) {
    const user = useAuthStore((s) => s.user);
    const openModal = useAuthModal((s) => s.openModal);
    const [active, setActive] = useState(false);
    const [pending, setPending] = useState(false);

    useEffect(() => {
        if (!fetchStatus || !user) return;
        let cancelled = false;
        getStockAlertStatus(productId, getActiveStoreId() || 0)
            .then((status) => {
                if (!cancelled) setActive(status.active);
            })
            .catch(() => {
                /* estado inicial é opcional — silencia */
            });
        return () => {
            cancelled = true;
        };
    }, [fetchStatus, user, productId]);

    /** Registra o aviso (ou cancela, quando já ativo). */
    async function toggle() {
        if (pending) return;
        if (!user) {
            openModal("login");
            return;
        }

        setPending(true);
        try {
            const storeId = getActiveStoreId() || 0;
            if (active) {
                const status = await cancelStockAlert(productId, storeId);
                setActive(status.active);
                toast.success("Aviso cancelado.");
            } else {
                const status = await requestStockAlert(productId, storeId);
                setActive(status.active);
                toast.success("Você será avisado quando o produto chegar.");
            }
        } catch (error) {
            toast.error(getApiErrorMessage(error, "Não foi possível registrar o aviso."));
        } finally {
            setPending(false);
        }
    }

    return { active, pending, toggle };
}
